import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import type { KeyHighlight } from '../../visual-assist/types.js';
import { MIDI_A0, MIDI_C8 } from '../../visual-assist/types.js';
import type { HighlightType } from './piano-key.js';
import './piano-key.js';

/** Black key semitone offsets within an octave (C=0) */
const BLACK_KEY_OFFSETS = [1, 3, 6, 8, 10]; // C#, D#, F#, G#, A#

function isBlackKey(midiNote: number): boolean {
    const semitone = midiNote % 12;
    return BLACK_KEY_OFFSETS.includes(semitone);
}

@customElement('piano-keyboard')
export class PianoKeyboard extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                justify-content: center;
                position: relative;
                height: 120px;
            }

            @media (max-width: 768px) {
                :host {
                    height: 100px;
                }
            }

            @media (max-width: 480px) {
                :host {
                    height: 80px;
                }
            }
        `,
    ];

    @property({ attribute: false })
    highlights: KeyHighlight[] = [];

    @property({ type: Number, attribute: 'start-midi' })
    startMidi = MIDI_A0;

    @property({ type: Number, attribute: 'end-midi' })
    endMidi = MIDI_C8;

    render() {
        // Build a lookup map from highlights for O(1) access
        const highlightMap = new Map<number, KeyHighlight>();
        for (const h of this.highlights) {
            highlightMap.set(h.midiNote, h);
        }

        const keys = [];
        for (let midi = this.startMidi; midi <= this.endMidi; midi++) {
            const black = isBlackKey(midi);
            const h = highlightMap.get(midi);

            let highlight: HighlightType = 'none';
            let faded = false;
            let fingering = '';

            if (h) {
                highlight = h.color;
                faded = !!h.faded;
                if (!faded && h.fingering !== null && h.fingering >= 1 && h.fingering <= 5) {
                    fingering = String(h.fingering);
                }
            }

            keys.push(html`<piano-key
                .midi=${midi}
                ?black=${black}
                highlight=${highlight}
                ?faded=${faded}
                fingering=${fingering}
            ></piano-key>`);
        }

        return html`${keys}`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'piano-keyboard': PianoKeyboard;
    }
}
